<template>
  <div
    ref="root"
    class="rd rd--guest"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('guest_report')" @select="openScreen">
        <template #tools>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
            :title="`${$t('report_desktop.shortcuts')} (?)`"
            :aria-label="$t('report_desktop.shortcuts')"
            :aria-expanded="String(helpOpen)"
            @click="helpOpen = !helpOpen"
          >
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
          <button
            type="button"
            class="ui-text-btn rd-nav__legacy"
            :title="$t('report_desktop.old_view')"
            @click="openLegacy"
          >
            <v-icon size="16" color="#c2410c">mdi-history</v-icon>
            <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
          </button>
        </template>
      </report-nav>

      <report-filter-bar
        ref="bar"
        :from="from"
        :to="to"
        :presets="presets"
        :quick-keys="quickKeys"
        :companies="availableRestaurants"
        :selected-ids="selectedRestaurantIds"
        :company-mode="restaurantMode"
        :labels="restaurantLabels"
        :search="search"
        :loading="loading"
        @period="onPeriod"
        @companies="onRestaurants"
        @update:search="search = $event"
        @refresh="reload"
        @print="print"
        @excel="excel"
      ></report-filter-bar>

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ screen.title }}</h1>
            <p class="rd-head__sub">{{ headerSubtitle }}</p>
          </div>
          <div class="rd-head__right">
            <report-kpis :kpis="kpis"></report-kpis>
            <button type="button" class="ui-btn ui-btn--primary" @click="openKitchenDisplay">
              <v-icon size="16" color="#ffffff">mdi-chef-hat</v-icon>
              {{ $t("kitchen_display") }}
            </button>
          </div>
        </div>

        <report-state
          v-if="needsRestaurants"
          icon="mdi-silverware"
          :title="$t('select_restaurant')"
          :text="$t('please_select_restaurant_to_view_data')"
        ></report-state>
        <report-desktop-legacy
          v-else-if="screen.legacy"
          :key="`${currentKey}-${reloadKey}`"
          :component="screen.component"
          :component-props="screenBindings"
          :load-on-mount="isAdmin"
          @toast="showToast"
        ></report-desktop-legacy>
        <component
          :is="screen.component"
          v-else
          :key="`${currentKey}-${reloadKey}`"
          v-bind="screenBindings"
          @update:status="status = $event"
          @update:delivery="delivery = $event"
          @kpis="kpis = $event"
          @loading="loading = $event"
          @open-panel="openPanel"
          @toast="showToast"
        ></component>
      </div>
    </div>

    <report-detail-panel
      :stack="panelStack"
      @close="closePanel"
      @back="panelBack"
      @open="openPanel"
      @toast="showToast"
    ></report-detail-panel>
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false"></report-help-popover>
    <ui-toast :toast="toast" @action="runToastAction"></ui-toast>
  </div>
</template>

<script>
import moment from "moment";
import "@/components/report/desktop/reportDesktop.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportKpis from "@/components/report/shared/ReportKpis.vue";
import ReportFilterBar from "@/components/report/desktop/ReportFilterBar.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportDesktopLegacy from "@/components/report/desktop/screens/ReportDesktopLegacy.vue";
import hasPlural from "@/components/report/shared/hasPlural";
import hasReportShell from "@/components/report/shared/hasReportShell";
import { buildGuestPeriodPresets, formatRange, formatShortDate } from "@/components/report/shared/periods";
import { declaredProps } from "./declaredProps";
import {
  DEFAULT_GUEST_SCREEN,
  ROLE_ADMIN,
  buildGuestMenu,
  buildGuestScreens,
  roleOf,
} from "./guestReportScreens";
import {
  GUEST_QUERY,
  LEGACY_UI_VALUE,
  MANAGED_GUEST_QUERY,
  parseDelivery,
  parseIds,
  parseRange,
  parseStatus,
  serializeIds,
} from "./guestReportQuery";

const ISO = "YYYY-MM-DD";
const QUICK_KEYS = ["today", "yesterday", "this_week"];

/**
 * Izveštaj gost na računaru: gornja navigacija, traka filtera (period, restorani za admina,
 * pretraga), zaglavlje sa brojkama, izveštaj i panel sa desne strane. Stanje ide u adresu.
 * Narudžbe su novi ekran; ostali izveštaji su stari tabovi u novoj ljusci (štampa i Excel rade
 * isto kao do sada). Raspored, panel, prečice i obaveštenja su u hasReportShell.
 */
export default {
  name: "GuestReportDesktop",
  mixins: [hasReportShell, hasPlural],
  components: {
    ReportNav,
    ReportHelpPopover,
    ReportKpis,
    ReportFilterBar,
    ReportDetailPanel,
    ReportState,
    ReportDesktopLegacy,
  },
  data() {
    const { query } = this.$route;
    const range = parseRange(query[GUEST_QUERY.from], query[GUEST_QUERY.to]);
    const today = moment().format(ISO);
    return {
      from: range ? range[0] : today,
      to: range ? range[1] : today,
      status: parseStatus(query[GUEST_QUERY.status]),
      delivery: parseDelivery(query[GUEST_QUERY.delivery]),
      search: "",
      pendingSearch: "",
      selectedRestaurantIds: [],
      quickKeys: QUICK_KEYS,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    role() {
      return roleOf(this.user?.type);
    },
    isAdmin() {
      return this.role === ROLE_ADMIN;
    },
    presets() {
      return buildGuestPeriodPresets();
    },
    dateRange() {
      return [this.from, this.to];
    },
    screens() {
      return buildGuestScreens((key, params) => this.$t(key, params), this.role);
    },
    menuGroups() {
      return buildGuestMenu((key, params) => this.$t(key, params), this.role).map((group) => ({
        ...group,
        items: group.items.map((key) => ({ key, ...this.screens[key] })),
      }));
    },
    menuKeys() {
      return this.menuGroups.flatMap((group) => group.items.map((item) => item.key));
    },
    currentKey() {
      const wanted = this.$route.query[GUEST_QUERY.report];
      return this.menuKeys.includes(wanted) ? wanted : DEFAULT_GUEST_SCREEN;
    },
    screen() {
      return this.screens[this.currentKey];
    },
    /** Restorani koje admin može da izabere (oni koji primaju narudžbe bez ugovora). */
    availableRestaurants() {
      if (!this.isAdmin) {
        return [];
      }
      return (this.user?.restaurants || []).filter((item) => item.settings && item.settings.lice_bez_ugovora);
    },
    restaurantMode() {
      return this.isAdmin && this.screen.restaurants ? "multi" : null;
    },
    selectedRestaurants() {
      return this.availableRestaurants.filter((item) => this.selectedRestaurantIds.includes(item.id));
    },
    restaurantIds() {
      return this.selectedRestaurants.map((item) => item.id).join(",");
    },
    needsRestaurants() {
      return this.restaurantMode === "multi" && !this.restaurantIds;
    },
    restaurantLabels() {
      return {
        title: this.$t("restaurants"),
        search: this.$t("guest_desktop.search_restaurants"),
        pick: this.$t("select_restaurant"),
        all: (n) => this.$t("guest_desktop.all_restaurants_n", { n }),
        count: (n) => this.pluralT("guest_desktop.restaurants_n", n),
      };
    },
    /** Sve što ekrani mogu da dobiju; svaki ekran uzima samo ono što deklariše. */
    allBindings() {
      return {
        searchQuery: this.search,
        dateRange: this.dateRange,
        selectedDate: this.from,
        selectedStatus: { id: 0, name: "all" },
        selectedDeliveryType: { id: 3, name: "all" },
        selectedRestaurants: this.selectedRestaurants,
        restaurantIds: this.restaurantIds,
        isActiveTab: true,
        status: this.status,
        delivery: this.delivery,
      };
    },
    screenBindings() {
      const all = this.allBindings;
      return declaredProps(this.screen.component).reduce((bindings, key) => {
        if (key in all) {
          bindings[key] = all[key];
        }
        return bindings;
      }, {});
    },
    formatLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = new Intl.DateTimeFormat(this.formatLocale, { weekday: "long" }).format(
        moment(this.from, ISO).toDate()
      );
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formatShortDate(this.from, true)}`;
    },
    headerSubtitle() {
      const parts = [this.periodLong];
      if (this.restaurantMode === "multi") {
        parts.push(
          this.selectedRestaurants.length === 1
            ? this.toLatin(this.selectedRestaurants[0].name)
            : this.pluralT("guest_desktop.restaurants_n", this.selectedRestaurants.length)
        );
      }
      return parts.filter(Boolean).join(" · ");
    },
    /** Stanje koje ide u adresu (današnji dan i podrazumevane vrednosti se izostavljaju). */
    urlQuery() {
      const today = moment().format(ISO);
      const query = { [GUEST_QUERY.report]: this.currentKey };
      if (!(this.from === today && this.to === today)) {
        query[GUEST_QUERY.from] = this.from;
        query[GUEST_QUERY.to] = this.to;
      }
      if (this.status && this.currentKey === "orders") {
        query[GUEST_QUERY.status] = this.status;
      }
      if (this.delivery !== null && this.currentKey === "orders") {
        query[GUEST_QUERY.delivery] = String(this.delivery);
      }
      if (this.isAdmin) {
        const ids = serializeIds(this.selectedRestaurantIds, this.availableRestaurants.length);
        if (ids) {
          query[GUEST_QUERY.restaurants] = ids;
        }
      }
      return query;
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    currentKey(key, previous) {
      if (key === previous) {
        return;
      }
      this.kpis = [];
      this.loading = false;
      this.closePanel();
      this.search = this.pendingSearch;
      this.pendingSearch = "";
      window.scrollTo(0, 0);
    },
    // novi izbor filtera menja listu: otvoren panel više ne odgovara onome što se vidi
    status() {
      this.closePanel();
    },
    delivery() {
      this.closePanel();
    },
  },
  created() {
    // admin: izbor restorana iz adrese, inače svi
    const ids = parseIds(this.$route.query[GUEST_QUERY.restaurants]);
    const fromUrl = this.availableRestaurants.filter((item) => ids.includes(item.id)).map((item) => item.id);
    this.selectedRestaurantIds = fromUrl.length ? fromUrl : this.availableRestaurants.map((item) => item.id);
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  methods: {
    onPeriod({ from, to }) {
      if (from === this.from && to === this.to) {
        return;
      }
      this.closePanel();
      this.from = from;
      this.to = to;
    },
    onRestaurants(ids) {
      this.selectedRestaurantIds = this.availableRestaurants.filter((item) => ids.includes(item.id)).map((item) => item.id);
    },
    shiftDay(step) {
      const move = (date) => moment(date, ISO).add(step, "day").format(ISO);
      this.onPeriod({ from: move(this.from), to: move(this.to) });
    },
    openScreen(key, { search = "" } = {}) {
      if (!this.menuKeys.includes(key)) {
        return;
      }
      if (key === this.currentKey) {
        this.search = search;
        return;
      }
      this.pendingSearch = search;
      this.$router.push({ query: { ...this.$route.query, [GUEST_QUERY.report]: key } }).catch(() => {});
    },
    writeUrl(query) {
      const current = this.$route.query;
      const kept = Object.keys(current).reduce((acc, key) => {
        if (!MANAGED_GUEST_QUERY.includes(key)) {
          acc[key] = current[key];
        }
        return acc;
      }, {});
      const next = { ...kept, ...query };
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.keys(next).every((key) => String(current[key]) === String(next[key]));
      if (!same) {
        this.$router.replace({ query: next }).catch(() => {});
      }
    },
    print() {
      this.$store.commit("SET_PRINTING");
    },
    excel() {
      this.$store.commit("SET_EXCEL");
    },
    reload() {
      this.closePanel();
      this.reloadKey += 1;
    },
    openKitchenDisplay() {
      this.$router.push({ name: "kitchen-display" });
    },
    /** Stara stranica (tabovi): ?ui=stari, dok se novi izgled ne potvrdi. */
    openLegacy() {
      this.$router.replace({ query: { ...this.$route.query, [GUEST_QUERY.legacy]: LEGACY_UI_VALUE } }).catch(() => {});
    },
  },
};
</script>
